import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);

export const EXCEL_DATE_FORMAT = 'dd-mmm-yyyy';
export const EXCEL_DATETIME_FORMAT = 'yyyy-mm-dd hh:mm:ss';
export const EXCEL_TIME_FORMAT = 'hh:mm AM/PM';

export function toExcelSerialDate(value) {
    if (value == null || value === '' || value === '---') return null;
    const d = dayjs(value);
    if (!d.isValid()) return null;
    const utc = Date.UTC(d.year(), d.month(), d.date());
    return (utc - EXCEL_EPOCH_MS) / 86400000;
}

export function toExcelSerialDateTime(value) {
    if (value == null || value === '') return null;
    const d = dayjs(value);
    if (!d.isValid()) return null;
    return (d.valueOf() - EXCEL_EPOCH_MS) / 86400000;
}

export function resolveColumnFormat(heading) {
    if (heading?.isDateTime || heading?.type === 'datetime') return EXCEL_DATETIME_FORMAT;
    if (heading?.type === 'time') return EXCEL_TIME_FORMAT;
    if (heading?.isDate || heading?.type === 'date') return EXCEL_DATE_FORMAT;
    return null;
}

export function isDateColumnKey(key, heading) {
    if (
        heading?.isDate === false &&
        heading?.isDateTime === false &&
        heading?.type !== 'date' &&
        heading?.type !== 'time' &&
        heading?.type !== 'datetime'
    ) {
        return false;
    }
    if (
        heading?.isDate ||
        heading?.isDateTime ||
        heading?.type === 'date' ||
        heading?.type === 'time' ||
        heading?.type === 'datetime'
    ) {
        return true;
    }
    const patterns = [
        /_date$/i,
        /\.due_date$/i,
        /\.payment_date$/i,
        /^date$/i,
        /^created_at$/i,
        /^voucher_date$/i,
        /^transaction_date$/i,
        /^restored_at$/i,
        /^deactivated_at$/i,
        /^extended_at$/i,
        /renewed_date$/i,
        /start_date$/i,
        /end_date$/i,
    ];
    return patterns.some((pattern) => pattern.test(key));
}

function resolveCellValue(rawValue, heading) {
    if (rawValue === '---') return '---';
    const useDateTime =
        heading?.isDateTime || heading?.type === 'datetime' || heading?.type === 'time';
    const serial = useDateTime ? toExcelSerialDateTime(rawValue) : toExcelSerialDate(rawValue);
    return serial ?? '';
}

export function applyDateColumnFormats(worksheet, customHeadings) {
    if (!worksheet['!ref']) return;
    const range = XLSX.utils.decode_range(worksheet['!ref']);

    customHeadings.forEach((heading, colIndex) => {
        const format = resolveColumnFormat(heading);
        if (!format || !isDateColumnKey(heading.key, heading)) return;

        for (let r = 1; r <= range.e.r; r++) {
            const cellRef = XLSX.utils.encode_cell({ r, c: colIndex });
            const cell = worksheet[cellRef];
            if (!cell || cell.v === '' || cell.v == null) continue;
            if (typeof cell.v === 'number') {
                cell.t = 'n';
                cell.z = format;
            }
        }
    });
}

export function applyDateFormatsByLabels(worksheet, labelFormatMap) {
    if (!worksheet['!ref']) return;
    const range = XLSX.utils.decode_range(worksheet['!ref']);

    Object.entries(labelFormatMap).forEach(([label, format]) => {
        let colIndex = -1;
        for (let c = range.s.c; c <= range.e.c; c++) {
            const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c })];
            if (headerCell?.v === label) {
                colIndex = c;
                break;
            }
        }
        if (colIndex < 0) return;

        for (let r = 1; r <= range.e.r; r++) {
            const cellRef = XLSX.utils.encode_cell({ r, c: colIndex });
            const cell = worksheet[cellRef];
            if (!cell || typeof cell.v !== 'number') continue;
            cell.t = 'n';
            cell.z = format;
        }
    });
}

export function applyDateFormatToColumnFromRow(worksheet, colIndex, startRow, format = EXCEL_DATE_FORMAT) {
    if (!worksheet['!ref']) return;
    const range = XLSX.utils.decode_range(worksheet['!ref']);

    for (let r = startRow; r <= range.e.r; r++) {
        const cellRef = XLSX.utils.encode_cell({ r, c: colIndex });
        const cell = worksheet[cellRef];
        if (!cell || typeof cell.v !== 'number') continue;
        cell.t = 'n';
        cell.z = format;
    }
}

export function createExcelAndDownload(reportName, data, customHeadings, options = {}) {
    const { transformCell } = options;

    const formattedData = data.length
        ? data.map((item) => {
            const row = {};
            customHeadings.forEach((heading) => {
                const { key, label } = heading;
                let value = key.split('.').reduce((acc, part) => (acc ? acc[part] : null), item);

                if (transformCell) {
                    value = transformCell(key, value, item, heading);
                }

                if (isDateColumnKey(key, heading)) {
                    row[label] = resolveCellValue(value, heading);
                } else {
                    row[label] = value ?? '';
                }
            });
            return row;
        })
        : [Object.fromEntries(customHeadings.map(({ label }) => [label, '']))];

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    applyDateColumnFormats(worksheet, customHeadings);

    worksheet['!cols'] = customHeadings.map(({ width }) => ({ width: width || 20 }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), `${reportName}.xlsx`);
}

export function finalizeWorksheetDates(worksheet, labelFormatMap) {
    applyDateFormatsByLabels(worksheet, labelFormatMap);
    return worksheet;
}
